// ThemeContext - provides the effective MUI theme mode
// Uses MUI's native palette.mode for dark/light theming
// Also syncs data-theme attribute on document for CSS variable fallback

import React, { createContext, useContext, useMemo, useLayoutEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useFeatureFlags } from './FeatureFlagContext';

const ThemeContext = createContext({
  mode: 'light',
  isDarkMode: false,
});

// Light palette based on Variables.css :root values
const lightPalette = {
  mode: 'light',
  primary: {
    main: '#0c1e5c',
    light: '#387fcc',
    dark: '#0056b3',
  },
  secondary: {
    main: '#7f7bad',
    light: '#a9a5d4',
    dark: '#5a5690',
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#8b3e47',
  },
  warning: {
    main: '#856404',
    light: '#ffeaa7',
    dark: '#6d5200',
  },
  info: {
    main: '#1976d2',
    light: '#90caf9',
    dark: '#1565c0',
  },
  success: {
    main: '#22c55e',
    light: '#bbf7d0',
    dark: '#15803d',
  },
  background: {
    default: '#ffffff',
    paper: '#ffffff',
  },
  text: {
    primary: '#333333',
    secondary: '#555555',
    disabled: '#a0a0a0',
  },
  divider: '#dddddd',
  action: {
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
  },
};

// Dark palette based on Variables.css [data-theme="dark"] values
const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#4a90e2',
    light: '#6ba3f2',
    dark: '#2c5aa0',
  },
  secondary: {
    main: '#9490c7',
    light: '#beb9e4',
    dark: '#7f7bad',
  },
  error: {
    main: '#f87171',
    light: '#fca5a5',
    dark: '#b85c5c',
  },
  warning: {
    main: '#fbbf24',
    light: '#fde68a',
    dark: '#d4a00a',
  },
  info: {
    main: '#60a5fa',
    light: '#93c5fd',
    dark: '#3b82f6',
  },
  success: {
    main: '#4ade80',
    light: '#86efac',
    dark: '#22c55e',
  },
  background: {
    default: '#1a1a1a',
    paper: '#242424',
  },
  text: {
    primary: '#e0e0e0',
    secondary: '#b0b0b0',
    disabled: '#606060',
  },
  divider: '#404040',
  action: {
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(255, 255, 255, 0.16)',
    disabled: 'rgba(255, 255, 255, 0.3)',
  },
};

// Create theme based on mode
const getTheme = (mode) => createTheme({
  palette: mode === 'dark' ? darkPalette : lightPalette,
  typography: {
    fontFamily: '"Poppins", "Nunito", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.3s ease',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '3px', // Match --border-radius-small
        },
      },
    },
  },
});

export const ThemeContextProvider = ({ children }) => {
  const { flags, isLoading } = useFeatureFlags();
  const mode = !isLoading && flags?.useDarkMode === true ? 'dark' : 'light';

  // Preference writes belong to Settings. Policy resolution only controls the
  // rendered theme; disabling the feature never erases a saved preference.
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  // Memoize theme to prevent unnecessary re-renders
  const theme = useMemo(() => getTheme(mode), [mode]);

  const contextValue = useMemo(() => ({
    mode,
    isDarkMode: mode === 'dark',
  }), [mode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

export default ThemeContext;
